// src/core/composables/useEmployeesApi.ts
import { computed, ref } from "vue";
import { employeeService } from "@/core/services/employee.service";
import type {
  Employee,
  PagedResponse,
  EmployeeSearchCriteria,
  EmployeeExpiredResume,
  EmployeeExpiredResumeSearchCriteria,
} from "@/models";
import { RoleEnum } from "@/core/enum";
import { useAlerts } from "@/shared/composables/useAlerts";
import { usePermissions } from "@/core/composables/usePermissions";
import { useAppContextStore } from "@/core/store";
import { createApiComposable } from "@/core/composables/createApiComposable";

type UseEmployeeApiEvents = {
  onEmployeeChanged?: () => void;
};

export function useEmployeesApi(events: UseEmployeeApiEvents = {}) {
  const employees = ref<PagedResponse<Employee>>({} as PagedResponse<Employee>);
  const employee = ref<Employee>({} as Employee);
  const employeesExport = ref<Employee[]>([]);
  const employeeExpiredResume = ref<PagedResponse<EmployeeExpiredResume>>(
    {} as PagedResponse<EmployeeExpiredResume>
  );

  const { errorToast, successToast } = useAlerts();
  const api = createApiComposable({
    successToast,
    errorToast,
    defaultErrorMessage: "Request failed",
  });

  // UI/context concerns stay here (not in the service)
  const contextStore = useAppContextStore();
  const context = computed(() => contextStore.context);
  const { isManager, isEditor } = usePermissions(context.value.user);

  const applyRoleBasedCriteria = (criteria: EmployeeSearchCriteria): EmployeeSearchCriteria => {
    const next: EmployeeSearchCriteria = { ...criteria };

    if (isManager.value) {
      next.managerId = context.value.user.id;
      next.temporary = undefined;
    } else if (isEditor.value) {
      next.temporary = true;
      next.managerId = undefined;
    }

    return next;
  };

  const fetchEmployees = async (criteria: EmployeeSearchCriteria) => {
    const adjusted = applyRoleBasedCriteria(criteria);

    const data = await api.run(
      () => employeeService.fetchEmployees(adjusted),
      "Failed to load employees"
    );

    employees.value = data;
    events.onEmployeeChanged?.();
    return data;
  };

  const fetchEmployeesExport = async (criteria: EmployeeSearchCriteria) => {
    // apply role criteria too if export should follow the same rules
    const adjusted = applyRoleBasedCriteria(criteria);

    const data = await api.run(
      () => employeeService.fetchEmployeesExport(adjusted),
      "Failed to export employees"
    );

    employeesExport.value = data;
    return data;
  };

  const fetchEmployeeById = async (id: string) => {
    const data = await api.run(
      () => employeeService.fetchEmployeeById(id),
      "Failed to fetch employee"
    );

    employee.value = data;
    return data;
  };

  const postRole = async (employeeId: string, role: RoleEnum) => {
    const data = await api.runWithSuccess(
      () => employeeService.postRole(employeeId, role),
      "Saved",
      "Role updated",
      "Failed to update employee role"
    );

    employee.value = data;
    events.onEmployeeChanged?.();
    return data;
  };

  const fetchEmployeesExpiredResumes = async (criteria: EmployeeExpiredResumeSearchCriteria) => {
    const data = await api.run(
      () => employeeService.fetchEmployeesExpiredResumes(criteria),
      "Failed to fetch expired resumes"
    );

    employeeExpiredResume.value = data;
    return data;
  };

  const updateEmployee = async (updatedEmployee: Employee) => {
    const data = await api.runWithSuccess(
      () => employeeService.updateEmployee(updatedEmployee),
      "Saved",
      "Employee updated",
      "Failed to update employee"
    );

    employee.value = data;
    events.onEmployeeChanged?.();
    return data;
  };

  const toggleBilingualStatus = async (employeeId: string, bilingual: boolean) => {
    const data = await api.runWithSuccess(
      () => employeeService.toggleBilingualStatus(employeeId, bilingual),
      "Success",
      "Employee bilingual status toggled successfully",
      "Failed to toggle employee bilingual status"
    );

    employee.value = data;
    events.onEmployeeChanged?.();
    return data;
  };

  const deleteEmployee = async (employeeId: string) => {
    await api.runVoidWithSuccess(
      () => employeeService.deleteEmployee(employeeId),
      "Deleted",
      "Employee deleted",
      "Failed to delete employee"
    );

    events.onEmployeeChanged?.();
  };

  const createBlankEmployee = async (
    id: string,
    email: string,
    firstName: string | null,
    lastName: string | null
  ) => {
    const data = await api.runWithSuccess(
      () => employeeService.createBlankEmployee(id, email, firstName, lastName),
      "Created",
      "Blank employee created",
      "Failed to create blank employee"
    );

    employee.value = data;
    events.onEmployeeChanged?.();
    return data;
  };

  return {
    // reactive state
    employees,
    employeesExport,
    employeeExpiredResume,
    employee,

    // shared api state
    loading: api.loading,
    error: api.error,
    clearError: api.clearError,

    // actions
    fetchEmployees,
    fetchEmployeesExport,
    fetchEmployeeById,
    fetchEmployeesExpiredResumes,
    createBlankEmployee,
    postRole,
    updateEmployee,
    toggleBilingualStatus,
    deleteEmployee,
  };
}
